<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <modal
      v-if="isEditable"
      id="places_file_upload_modal"
      :visible="isVisible"
      :title="capitalize($t('actions.uploadFile'))"
      :body="capitalize($t('messages.uploadLocationsCSVFile'))"
      :form-fields="uploadCSVFileModalFormFields"
      :button1-caption="capitalize($t('actions.upload'))"
      :button2-caption="capitalize($t('actions.cancel'))"
      :button2-visible="true"
      @confirmed="csvUploadModalConfirmed"
      @cancelled="csvUploadModalCancelled">
      <template #modal-after-form>
        <div class="flex-v-full">
          <span
            v-if="uploadError != null"
            class="alert alert-danger"
            >{{ uploadError }}</span
          >

          <div class="modal-section-separator">
            <div class="line" />
            <div class="text">
              {{ capitalize($t('actions.downloadATemplate')) }}
            </div>
            <div class="line" />
          </div>
          <div class="file-templates">
            <div class="file-template">
              <a
                download="geofence-upload-template.csv"
                :href="templateCSVData">
                <icon-file-c-s-v />
              </a>
            </div>

            <div class="file-template">
              <a
                download="geofence-upload-template.xlsx"
                href="#"
                @click="downloadTemplateXLSXData">
                <icon-file-x-l-s />
              </a>
            </div>
          </div>
          <span class="alert alert-muted">{{ $t('messages.uploadPlacesInstructions') }}</span>
        </div>
      </template>
    </modal>

    <modal
      v-if="isEditable"
      :visible="isInvalidCSVFileModalVisible"
      :title="capitalize($t('actions.uploadFile'))"
      :body="capitalize($t('messages.invalidCSVFile'))"
      :button1-caption="capitalize($t('actions.ok'))"
      @confirmed="isInvalidCSVFileModalVisible = false">
      <template #modal-after-form>
        <div>
          <p v-if="invalidFileAdditionalDetails != null">{{ invalidFileAdditionalDetails }}</p>
          <ul v-if="missingColumnNames != null">
            <li
              v-for="col in missingColumnNames"
              :key="col">
              {{ capitalize(col) }}
            </li>
          </ul>
          <a
            download="geofence-upload-template.csv"
            :href="templateCSVData"
            >{{ $t('messages.downloadTemplateCSVFile') }}</a
          >
        </div>
      </template>
    </modal>

    <modal
      v-if="isEditable"
      :visible="isInvalidXLSFileModalVisible"
      :title="capitalize($t('actions.uploadFile'))"
      :body="capitalize($t('messages.invalidXLSFile'))"
      :button1-caption="capitalize($t('actions.ok'))"
      @confirmed="isInvalidXLSFileModalVisible = false">
      <template #modal-after-form>
        <div>
          <p v-if="invalidFileAdditionalDetails != null">{{ invalidFileAdditionalDetails }}</p>
          <ul v-if="missingColumnNames != null">
            <li
              v-for="col in missingColumnNames"
              :key="col">
              {{ capitalize(col) }}
            </li>
          </ul>
          <a
            download="geofence-upload-template.xls"
            @click="downloadTemplateXLSXData"
            >{{ $t('messages.downloadTemplateXLSFile') }}</a
          >
        </div>
      </template>
    </modal>

    <modal
      v-if="isEditable"
      id="places_file_upload_parsing_errors_modal"
      :visible="isCSVParsingErrorsModalVisible"
      :title="capitalize($t('actions.uploadFile'))"
      :body="capitalize($t('messages.theFollowingRowsContainErrors'))"
      :button1-caption="
        geofencesBeingImported.length > 0 ? capitalize($t('actions.importValidRows')) : capitalize($t('actions.ok'))
      "
      :button2-caption="capitalize($t('actions.dontImport'))"
      :button2-visible="geofencesBeingImported.length > 0"
      size="lg"
      @confirmed="csvParsingErrorsModalConfirmed"
      @cancelled="csvParsingErrorsModalCancelled">
      <template #modal-after-form>
        <div v-if="csvParsingErrors != null">
          <table class="table table-striped table-bordered errors-table">
            <colgroup>
              <col width="20" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>{{ capitalize($t('names.row', { n: 1 })) }}</th>
                <th>{{ capitalize($t('names.error', { n: 1 })) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="error in errors"
                :key="error.line">
                <td>{{ error.line }}</td>
                <td>{{ capitalize(error.message) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="file-errors-download">
            <a
              href="#"
              @click="downloadErrornousDataRows">
              <icon-file-x-l-s />
              {{ capitalize($t('actions.downloadRowsCausingErrors')) }}
            </a>
          </div>
          <p
            v-if="geofencesBeingImported.length > 0"
            style="margin-top: 1.6rem">
            {{ capitalize($t('messages.wouldYouLikeToImportTheValidRows')) }} ({{ geofencesBeingImported.length }})?
          </p>
        </div>
      </template>
    </modal>
  </div>
</template>

<script lang="ts">
  import { booleanValid } from '@turf/boolean-valid';
  import { multiPolygon, polygon } from '@turf/helpers';
  import Excel from 'exceljs';
  import { DateTime } from 'luxon';
  import numbro from 'numbro';
  import Papa from 'papaparse';
  import { capitalize } from 'vue';

  import { MapboxApi } from '@/common/apis/MapboxApi';
  import ModalFormField from '@/common/components/controls/modal/ModalFormField';
  import ModalFormFieldTypes from '@/common/components/controls/modal/ModalFormFieldTypes';
  import { IconFileCSV, IconFileXLS } from '@/common/icons/monochrome';
  import Geofence from '@/common/models/geo/Geofence';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { isNumeric } from '@/globals';
  import { generateCSVURI } from '@/utils/CsvUtils';
  import DownloadUtils from '@/utils/DownloadUtils';
  import i18n from '@/vueI18n';

  const DataColumns = {
    name: 'Geofence name',
    description: 'Geofence description',
    address: 'Geofence address',
    latitude: 'Geofence latitude',
    longitude: 'Geofence longitude',
    radiusInMeters: 'Geofence radius (meters)',
    geoJSON: 'GeoJSON',
  };

  const SampleDatarow = [
    'Apple Campus',
    'Corporate headquarters of Apple Inc. from 1993 until 2017',
    '1 Infinite Loop, Cupertino, CA 95014',
    37.332,
    -122.030781,
    25,
    '',
  ];

  const SampleGeoJSONrow = [
    'Microsoft Campus',
    'Corporate headquarters of Microsoft Corporation since 1986',
    '',
    47.6419,
    -122.1273,
    '',
    '{"type":"Polygon","coordinates":[[[-122.1273, 47.6419],[-122.1373, 47.6469],[-122.1173, 47.6469],[-122.1273, 47.6419]]]}',
  ];

  const GeoJSONinstructions = [
    'For more information about GeoJSON formatting, please see the official documentation here: ',
    'https://datatracker.ietf.org/doc/html/rfc7946',
  ];

  export default {
    components: {
      IconFileCSV,
      IconFileXLS,
    },
    props: {
      geofenceGroup: {
        type: Object,
        default: null,
      },
      isEditable: {
        type: Boolean,
        default: false,
      },
      lastGeofencesImported: {
        type: Object,
      },
    },
    data() {
      return {
        csvParsingErrors: null,
        dataRowsThatFailedGeocoding: [],
        invalidFileAdditionalDetails: null,
        isCSVParsingErrorsModalVisible: false,
        isInvalidCSVFileModalVisible: false,
        isInvalidXLSFileModalVisible: false,
        isUploadingCSV: false,
        isVisible: false,
        geofencesBeingImported: [],
        missingColumnNames: null,
        templateCSVData: generateCSVURI([
          Object.values(DataColumns),
          SampleDatarow,
          this.escapeDoubleQuotes(SampleGeoJSONrow),
          [],
          GeoJSONinstructions,
        ]),
        uploadCSVFileModalFormFields: [
          new ModalFormField({
            name: 'files',
            caption: capitalize(i18n.global.t('names.file', { n: 1 })),
            inputType: ModalFormFieldTypes.file,
          }),
        ],
        uploadError: null,
      };
    },
    computed: {
      errors() {
        return [
          ...this.csvParsingErrors,
          ...this.dataRowsThatFailedGeocoding.map((x) => {
            return {
              line: x.index,
              message:
                x.error != null
                  ? `${i18n.global.t('messages.failedToGeocodeAddress')}. ${x.error}`
                  : `${i18n.global.t('messages.failedToGeocodeAddress')}.`,
              dataRow: x.dataRow,
            };
          }),
        ].sort((a, b) => a.line - b.line);
      },
    },
    methods: {
      capitalize(text) {
        return capitalize(text);
      },
      async processDataRows(dataRows) {
        const errors = [];
        const geofences = [];
        const validDataRows = [];

        // Parse columns
        const [firstRow] = dataRows;
        const missingRequiredColumns = Object.values(DataColumns).reduce(
          (mem, c) => (c === 'GeoJSON' || firstRow.includes(c) ? mem : [...mem, c]),
          [],
        );
        if (missingRequiredColumns.length > 0) {
          throw {
            error: {
              code: 'missing-columns',
              value: missingRequiredColumns,
            },
          };
        }

        const columnIndices = {
          name: firstRow.indexOf(DataColumns.name),
          description: firstRow.indexOf(DataColumns.description),
          address: firstRow.indexOf(DataColumns.address),
          latitude: firstRow.indexOf(DataColumns.latitude),
          longitude: firstRow.indexOf(DataColumns.longitude),
          radiusInMeters: firstRow.indexOf(DataColumns.radiusInMeters),
          geoJSON: firstRow.indexOf(DataColumns.geoJSON),
        };

        dataRows.forEach((x, i) => {
          if (i === 0) {
            return;
          }
          const name = `${x[columnIndices.name]}`;
          const description = `${x[columnIndices.description]}`;
          const address = x[columnIndices.address];
          let latText = `${x[columnIndices.latitude]}`;
          let lngText = `${x[columnIndices.longitude]}`;
          const radius = x[columnIndices.radiusInMeters];
          let geoJSON = x[columnIndices.geoJSON];

          geoJSON = geoJSON == null || geoJSON.trim() === '' ? null : geoJSON;

          // For french and german languages the Decimal(.) is considered as comma, so numbro treats 19.42 as 1942.
          // Because of this some validation fails. So it needs some formatting
          latText = this.convertDecimalsToLocaleSpecific(latText);
          lngText = this.convertDecimalsToLocaleSpecific(lngText);

          //
          // Validate data.
          //
          let isMissingLat = latText == null || `${latText}`.trim() === '' || `${latText}`.trim() == 'undefined';
          let isMissingLng = lngText == null || `${lngText}`.trim() === '' || `${lngText}`.trim() == 'undefined';
          const isMissingAddress = address == null || `${address}`.trim() === '';

          // Try to use first geoJSON coordinate if address, lat, and lng are missing
          if (isMissingAddress && isMissingLat && isMissingLng) {
            const [geoLng, geoLat] = this.getGeoJSONFirstCoord(geoJSON) || [];
            if (geoLat !== null && geoLng !== null) {
              latText = geoLat;
              lngText = geoLng;
              isMissingLat = false;
              isMissingLng = false;
            }
          }
          const isBadLat =
            latText == null ||
            !isNumeric(`${latText}`) ||
            numbro(`${latText}`).value() < -90 ||
            numbro(`${latText}`).value() > 90;
          const isBadLng =
            lngText == null ||
            !isNumeric(`${lngText}`) ||
            numbro(`${lngText}`).value() < -180 ||
            numbro(`${lngText}`).value() > 180;
          const errorLine = `${i + 1}`;
          let error = null;
          if (name == null || `${name}`.trim() === '') {
            error = { line: errorLine, message: `${capitalize(i18n.global.t('messages.nameIsMissing'))}`, dataRow: x };
          } else if (description == null || `${description}`.trim() === '') {
            error = {
              line: errorLine,
              message: `${capitalize(i18n.global.t('messages.descriptionIsMissing'))}`,
              dataRow: x,
            };
          } else if (isMissingAddress && isMissingLng && isMissingLat) {
            error = {
              line: errorLine,
              message: `${capitalize(i18n.global.t('messages.mustHaveALatLongOrAnAddress'))}`,
              dataRow: x,
            };
          } else if (isMissingAddress && isBadLat) {
            error = {
              line: errorLine,
              message: `${capitalize(i18n.global.t('messages.invalidLatitude'))}`,
              dataRow: x,
            };
          } else if (isMissingAddress && isBadLng) {
            error = {
              line: errorLine,
              message: `${capitalize(i18n.global.t('messages.invalidLongitude'))}`,
              dataRow: x,
            };
          } else if (
            geoJSON == null &&
            (radius == null ||
              numbro(`${radius}`).value() == null ||
              numbro(`${radius}`).value() <= 0 ||
              numbro(`${radius}`).value() > 100000)
          ) {
            error = { line: errorLine, message: `${capitalize(i18n.global.t('messages.invalidRadius'))}`, dataRow: x };
          } else if (geoJSON != null) {
            const geoJSONValidation = this.validateGeoJSON(geoJSON);
            if (!geoJSONValidation.valid) {
              error = {
                line: errorLine,
                message: `${capitalize(i18n.global.t(`messages.${geoJSONValidation.message}`))}`,
                dataRow: x,
              };
            }
          }

          if (error != null) {
            errors.push(error);
          } else {
            validDataRows.push({
              index: i + 2,
              name,
              description,
              address,
              lat: isMissingLat ? null : numbro(`${latText}`).value(),
              lng: isMissingLng ? null : numbro(`${lngText}`).value(),
              radius: geoJSON == null ? numbro(`${radius}`).value() : null,
              geoJSON,
              dataRow: x,
            });
          }
        });

        const createGeofences = () => {
          validDataRows.forEach((x) => {
            const radiusInKm = x.radius / 1000;
            const { name } = x;
            const { description } = x;
            const { lat } = x;
            const { lng } = x;
            const { geoJSON } = x;
            let validatedGeoJSON;

            if (geoJSON == null) {
              validatedGeoJSON = undefined;
            } else {
              validatedGeoJSON = JSON.parse(geoJSON);
              validatedGeoJSON.type = this.getValidGeoJSONType(validatedGeoJSON.type);
            }

            geofences.push(
              Geofence.fromObject({
                id: `${DateTime.local().toSeconds()}-${Math.round(Math.random() * 10000)}`,
                name,
                description,
                lat,
                lng,
                geofenceGroupID: this.geofenceGroup.id,
                tags: geoJSON == null ? `{ "isPoint": true, "radius": ${radiusInKm} }` : '{ "isPoint": false }',
                geoJson: validatedGeoJSON,
              }),
            );
          });

          this.geofencesBeingImported = geofences;

          this.csvParsingErrors = errors;
          if (errors.length > 0 || this.dataRowsThatFailedGeocoding.length > 0) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
            this.isVisible = false;
            this.isCSVParsingErrorsModalVisible = true;
          } else {
            this.$emit('addGeofences', this.geofencesBeingImported);
            GlobalEventBus.$emit(GlobalEvents.loaded);
            this.isVisible = false;
          }
        };
        const addressesNeedingGeocoding = validDataRows
          .filter((x) => x.lat == null && x.lng == null && x.address != null)
          .map((x) => x.address);

        this.dataRowsThatFailedGeocoding = [];

        if (addressesNeedingGeocoding.length === 0) {
          createGeofences();
        } else {
          const mapboxApi = MapboxApi.getInstance();

          for (const address of addressesNeedingGeocoding) {
            const dataRows = validDataRows.filter((x) => x.address === address);
            try {
              const mapboxGeocoding = await mapboxApi.forwardGeocode({ q: address });
              const [bestFeature] = mapboxGeocoding.features;
              const { latitude, longitude } = bestFeature.properties.coordinates;
              dataRows.forEach((dataRow) => {
                dataRow.lat = latitude;
                dataRow.lng = longitude;
              });
            } catch (error) {
              dataRows.forEach((dataRow) => {
                this.dataRowsThatFailedGeocoding.push({ ...dataRow, error });
                validDataRows.splice(validDataRows.indexOf(dataRow), 1); // remove the row from the valid datarow array.
              });
            }
          }
          createGeofences();
        }
      },
      validateGeoJSON(geoJSON) {
        try {
          const parsedGeoJSON = JSON.parse(geoJSON);
          const validatedType = this.getValidGeoJSONType(parsedGeoJSON.type);

          if (!validatedType) {
            return { valid: false, message: 'invalidGeoJSONType' };
          }

          parsedGeoJSON.type = validatedType;
          const createPolygon = validatedType === 'Polygon' ? polygon : multiPolygon;
          const polygonFromJSON = createPolygon(parsedGeoJSON.coordinates);

          return { valid: booleanValid(polygonFromJSON), message: 'invalidGeoJSON' };
        } catch (e) {
          // map English error messages to corresponding i18n keys for translation
          const errorMessages = {
            'First and last Position are not equivalent.': 'invalidGeoJSONRingClosure',
            'Each LinearRing of a Polygon must have 4 or more Positions.': 'invalidGeoJSONInsufficientPoints',
          };
          return { valid: false, message: errorMessages[e.message] || 'invalidGeoJSON' };
        }
      },
      getValidGeoJSONType(input) {
        const normalizedInput = input.trim().toLowerCase();
        if (normalizedInput === 'polygon') return 'Polygon';
        if (normalizedInput === 'multipolygon') return 'MultiPolygon';
        return null;
      },
      getGeoJSONFirstCoord(geoJSON) {
        try {
          const parsedGeoJSON = JSON.parse(geoJSON);
          const geoType = parsedGeoJSON?.type?.trim().toLowerCase();
          const coords = parsedGeoJSON?.coordinates;

          if (coords) {
            if (geoType === 'polygon') {
              return coords[0][0];
            } else if (geoType === 'multipolygon') {
              return coords[0][0][0];
            }
          }
        } catch (e) {
          return null;
        }
        return null;
      },
      async csvUploadModalConfirmed(formFields) {
        this.invalidFileAdditionalDetails = null;
        this.missingColumnNames = null;

        if (formFields[0].value == null) {
          return;
        }
        GlobalEventBus.$emit(GlobalEvents.loading);
        const file = formFields[0].value[0];
        if (file.type === 'text/csv' || file.name.indexOf('.csv') > -1) {
          this.isUploadingCSV = true;
          Papa.parse(file, {
            keepEmptyRows: false,
            skipEmptyLines: true,
            complete: async (result) => {
              if (result.errors.length > 0) {
                GlobalEventBus.$emit(GlobalEvents.loaded);
                this.isInvalidCSVFileModalVisible = true;
              } else {
                GlobalEventBus.$emit(GlobalEvents.loaded);
                const dataRows = result.data; // All rows after the first.
                try {
                  await this.processDataRows(dataRows);
                } catch (ex) {
                  switch (ex?.error?.code) {
                    case 'missing-columns':
                      this.invalidFileAdditionalDetails = i18n.global.t('messages.someColumnsAreMissing');
                      this.missingColumnNames = ex.error.value;
                      break;
                    default:
                      break;
                  }
                  this.isInvalidCSVFileModalVisible = true;
                }
              }
            },
          });
        } else if (
          file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.name.indexOf('.xlsx') > -1
        ) {
          this.isUploadingCSV = false;
          const fileReader = new window.FileReader();
          fileReader.onload = async (e) => {
            const workbook = new Excel.Workbook();
            try {
              await workbook.xlsx.load(e.target.result);
              const worksheet = workbook.getWorksheet(1);
              const dataRows = [];
              worksheet.eachRow((row) => {
                dataRows.push(row.values.slice(1)); // Index 0 is always empty, I guess to reflect Excel's 1-based index.
              });
              await this.processDataRows(dataRows); // Remove headers.
            } catch (xlsxError) {
              if (xlsxError.error) {
                switch (xlsxError.error.code) {
                  case 'missing-columns':
                    this.invalidFileAdditionalDetails = i18n.global.t('messages.someColumnsAreMissing');
                    this.missingColumnNames = xlsxError.error.value;
                    break;
                  default:
                    this.invalidFileAdditionalDetails = null;
                    this.missingColumnNames = [];
                    break;
                }
              }
              GlobalEventBus.$emit(GlobalEvents.loaded);
              this.isInvalidXLSFileModalVisible = true;
            }
          };
          fileReader.onerror = (e) => {
            GlobalEventBus.$emit(GlobalEvents.loaded);
            this.isInvalidXLSFileModalVisible = true;
            console.log(e);
          };
          fileReader.readAsArrayBuffer(file);
        } else {
          this.uploadError = capitalize(i18n.global.t('messages.unsupportedFileFormat'));
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      },
      csvUploadModalCancelled() {
        this.isVisible = false;
      },
      csvParsingErrorsModalConfirmed() {
        this.$emit('addGeofences', this.geofencesBeingImported);
        this.isCSVParsingErrorsModalVisible = false;
      },
      csvParsingErrorsModalCancelled() {
        this.isCSVParsingErrorsModalVisible = false;
      },
      escapeDoubleQuotes(csvRow) {
        return csvRow.map((item) => {
          if (typeof item === 'string') {
            return item.replace(/"/g, '""');
          }
          return item;
        });
      },
      show() {
        this.csvParsingErrors = null;
        this.isVisible = true;
      },
      downloadErrornousDataRows() {
        const workbook = new Excel.Workbook();

        workbook.views = [
          {
            x: 0,
            y: 0,
            width: 30000,
            height: 20000,
            firstSheet: 0,
            activeTab: 1,
            visibility: 'visible',
          },
        ];

        const sheet = workbook.addWorksheet('Places');
        const baseColumnWidth = 10;
        sheet.getColumn(1).width = 2 * baseColumnWidth;
        sheet.getColumn(2).width = 4 * baseColumnWidth;
        sheet.getColumn(3).width = 4 * baseColumnWidth;
        sheet.getColumn(4).width = baseColumnWidth;
        sheet.getColumn(5).width = baseColumnWidth;
        sheet.getColumn(6).width = 2 * baseColumnWidth;
        sheet.getColumn(7).width = 10 * baseColumnWidth;

        sheet.addRow([
          'Geofence name',
          'Geofence description',
          'Geofence address',
          'Geofence latitude',
          'Geofence longitude',
          'Geofence radius (meters)',
          'GeoJSON',
          'error',
        ]);
        this.errors.forEach((x) => {
          const data = [...x.dataRow];
          data[7] = x.message;
          sheet.addRow(data);
        });

        workbook.xlsx.writeBuffer().then((data) => {
          const blob = new window.Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          DownloadUtils.saveBlobToFile(blob, 'errors.xlsx');
        });
      },
      downloadTemplateXLSXData(e) {
        e.preventDefault();
        const workbook = new Excel.Workbook();

        workbook.views = [
          {
            x: 0,
            y: 0,
            width: 10000,
            height: 20000,
            firstSheet: 0,
            activeTab: 1,
            visibility: 'visible',
          },
        ];

        const sheet = workbook.addWorksheet('Places');
        sheet.addRows([Object.values(DataColumns), SampleDatarow, SampleGeoJSONrow, [], GeoJSONinstructions]);
        sheet.getCell(5, 9).value = {
          text: 'https://datatracker.ietf.org/doc/html/rfc7946',
          hyperlink: 'https://datatracker.ietf.org/doc/html/rfc7946',
        };
        sheet.mergeCells('A5:H5');
        sheet.mergeCells('I5:L5');

        workbook.xlsx.writeBuffer().then((data) => {
          const blob = new window.Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          DownloadUtils.saveBlobToFile(blob, 'geofence-upload-template.xlsx');
        });
      },
      convertDecimalsToLocaleSpecific(val) {
        let formattedDecimalValue = val;
        if (val != null && val.includes('.')) {
          formattedDecimalValue = val.replace('.', numbro.languageData().delimiters.decimal);
          return formattedDecimalValue;
        }
        return formattedDecimalValue;
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .file-templates {
    display: flex;
    justify-content: space-around;

    .file-template {
      display: flex;
      flex-direction: column;
      align-content: center;

      a {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      svg {
        stroke: var(--secondaryColor);
        fill: var(--secondaryColor);

        width: 4.8rem;
        height: 4.8rem;
        margin-right: 0.1rem;
        margin-bottom: 0.6rem;
      }

      &:first-of-type {
        svg {
          margin-left: 0.1rem;
          margin-right: 0;
        }
      }
    }
  }

  .file-errors-download {
    margin-top: 1.6rem;

    a {
      display: flex;
      flex-direction: row;
      align-content: center;

      svg {
        stroke: var(--secondaryColor);
        fill: var(--secondaryColor);

        width: 2rem;
        height: 2rem;
        margin-right: 0.6rem;
      }
    }
  }

  .alert-muted {
    margin-top: 1.6rem;
  }

  .errors-table {
    tbody {
      display: block;
      max-height: 30rem;
      overflow: auto;
    }

    thead,
    tbody tr {
      display: table;
      width: 100%;
      table-layout: fixed; /* even columns width , fix width of table too*/

      td,
      th {
        &:first-of-type {
          width: 5rem;
        }

        width: 100%;
      }
    }

    thead {
      width: calc(100% - 1em); /* scrollbar is average 1em/16px width, remove it from thead width */
    }

    table {
      width: 60rem;
    }
  }
</style>
